import { useQuery } from "@tanstack/react-query";
import { productQueries, type Product } from "@/api/product";

/**
 * Hook to fetch product pricing information
 * Useful for displaying credit costs in various parts of the application
 *
 * @returns {Object} Object containing products data, loading state, and error
 */
export function useProductPricing() {
  const {
    data: products = [],
    isLoading,
    error,
  } = useQuery(productQueries.products());

  // Filter for active products only
  const activeProducts = products.filter((product) => product.active);

  // Group products by family for easier display
  const productsByFamily = activeProducts.reduce<Record<string, Product[]>>(
    (acc, product) => {
      const family = product.product_family;
      if (!acc[family]) {
        acc[family] = [];
      }
      acc[family].push(product);
      return acc;
    },
    {},
  );

  // Get automation-specific products
  const automationProducts = activeProducts.filter(
    (product) => product.automation_product,
  );

  return {
    products: activeProducts,
    productsByFamily,
    automationProducts,
    isLoading,
    error,
  };
}
